/**
 * Formatting utility functions
 * 格式化相关工具函数
 */

/**
 * Get base URL for images
 * 获取图片的基础URL
 * 
 * @returns {String} Base URL for images
 */
function getImageBaseUrl() {
  try {
    const domain = window.location.hostname.split('.')[1]
    if (!domain) return ''
    return `https://www.${domain}.com/api/profile/`
  } catch (error) {
    console.error('Error getting image base URL:', error)
    return ''
  }
}

/**
 * Normalize image URL - add domain prefix if it's just a filename
 * 标准化图片URL - 如果只是文件名，则添加域名前缀
 * 
 * @param {String} url - Image URL or filename
 * @returns {String} Full image URL
 */
function normalizeImageUrl(url) {
  if (!url) return ''
  
  // If it's already a full URL (starts with http:// or https://), return as is
  if (/^https?:\/\//i.test(url)) {
    return url
  }
  
  // If it's just a filename, add the base URL
  const baseUrl = getImageBaseUrl()
  if (baseUrl) {
    return baseUrl + url
  }
  
  return url
}

/**
 * Parse image URLs from string
 * 从字符串中解析图片URL列表
 * 
 * Supports multiple delimiters: || (preferred), ### , (fallback)
 * Handles URLs with commas in them (e.g. Amazon URLs)
 * Now automatically adds domain prefix for filenames
 * 
 * @param {String} imgUrlString - Image URL string (comma-separated filenames or URLs)
 * @returns {Array} Array of image URLs
 */
export function parseImageUrls(imgUrlString) {
  if (!imgUrlString || typeof imgUrlString !== 'string') return []
  
  // Fallback to comma, but try to be smart about it
  const parts = imgUrlString.split(',').map(part => part.trim()).filter(Boolean)
  
  // Try to detect and merge parts that belong to the same URL
  const urls = []
  let currentUrl = ''
  
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]
    
    // If current URL is empty or this part looks like a new URL
    if (!currentUrl || isLikelyNewUrl(part, currentUrl)) {
      if (currentUrl) {
        urls.push(normalizeImageUrl(currentUrl))
      }
      currentUrl = part
    } else {
      // This part is likely a continuation of the current URL
      currentUrl += ',' + part
    }
  }
  
  if (currentUrl) {
    urls.push(normalizeImageUrl(currentUrl))
  }
  
  return urls
}

/**
 * Check if a part is likely the start of a new URL
 * 检查一个部分是否可能是新URL的开始
 */
function isLikelyNewUrl(part, currentUrl) {
  // If it starts with http:// or https://, it's definitely a new URL
  if (/^https?:\/\//i.test(part)) {
    return true
  }
  
  // If current URL looks complete (has common image extensions)
  if (currentUrl && /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(currentUrl)) {
    return true
  }
  
  // If the part looks like a complete path
  if (/^\//.test(part)) {
    return true
  }
  
  // Default: assume it's a continuation of the current URL
  return false
}

/**
 * Get first image URL from image URL string
 * 从图片URL字符串中获取第一个图片URL
 * 
 * @param {String} imgUrlString - Image URL string (comma-separated filenames or URLs)
 * @returns {String} First image URL or empty string
 */
export function getFirstImageUrl(imgUrlString) {
  const urls = parseImageUrls(imgUrlString)
  return urls[0] || ''
}

/**
 * Format date string to localized date
 * 格式化日期字符串
 * 
 * @param {String} dateString - Date string
 * @param {String} locale - Locale (default: 'en-US')
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {String} Formatted date string
 */
export function formatDate(dateString, locale = 'en-US', options = null) {
  if (!dateString) return ''
  
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateString)
      return ''
    }
    
    const defaultOptions = { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    }
    
    return date.toLocaleDateString(locale, options || defaultOptions)
  } catch (error) {
    console.error('Error formatting date:', error)
    return ''
  }
}

/**
 * Format content string by removing HTML tags and limiting items
 * 格式化内容字符串，移除HTML标签并限制条目数
 * 
 * @param {String} content - Content string
 * @param {Number} maxItems - Maximum number of items to show (default: 2)
 * @returns {String} Formatted content string
 */
export function formatContent(content, maxItems = 2) {
  if (!content) return ''
  
  try {
    return content
      .replace(/<br\s*\/?>/gi, ' ')
      .split(/[，,;]/)
      .filter(item => item.trim())
      .slice(0, maxItems)
      .join(' · ')
  } catch (error) {
    console.error('Error formatting content:', error)
    return content
  }
}

/**
 * Format price to fixed decimal places
 * 格式化价格为固定小数位
 * 
 * @param {Number|String} price - Price value
 * @param {Number} decimals - Number of decimal places (default: 2)
 * @returns {String} Formatted price string
 */
export function formatPrice(price, decimals = 2) {
  const num = parseFloat(price)
  if (isNaN(num)) return '0.00'
  return num.toFixed(decimals)
}

/**
 * Format number with thousands separator
 * 格式化数字，添加千分位分隔符
 * 
 * @param {Number|String} number - Number to format
 * @returns {String} Formatted number string
 */
export function formatNumber(number) {
  const num = parseFloat(number)
  if (isNaN(num)) return '0'
  return num.toLocaleString()
}

/**
 * Parse number string with commas
 * 解析带逗号的数字字符串
 * 
 * @param {String} numberStr - Number string (e.g., "4,227")
 * @returns {Number} Parsed number
 */
export function parseFormattedNumber(numberStr) {
  if (!numberStr) return 0
  if (typeof numberStr === 'number') return numberStr
  
  try {
    // Remove commas and parse
    return parseFloat(String(numberStr).replace(/,/g, ''))
  } catch (error) {
    console.error('Error parsing number:', error)
    return 0
  }
}

/**
 * Format credit card number with spaces
 * 格式化信用卡号，添加空格
 * 
 * @param {String} cardNumber - Card number
 * @returns {String} Formatted card number (XXXX XXXX XXXX XXXX)
 */
export function formatCardNumber(cardNumber) {
  if (!cardNumber) return ''
  
  // Remove all non-digit characters
  const cleaned = cardNumber.replace(/\D/g, '')
  
  // Add space every 4 digits
  return cleaned.match(/.{1,4}/g)?.join(' ') || cleaned
}

/**
 * Format phone number
 * 格式化电话号码
 * 
 * @param {String} phone - Phone number
 * @returns {String} Formatted phone number
 */
export function formatPhoneNumber(phone) {
  if (!phone) return ''
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '')
  
  // Format as (XXX) XXX-XXXX for 10 digits
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  
  return phone
}

/**
 * Truncate text to specified length
 * 截断文本到指定长度
 * 
 * @param {String} text - Text to truncate
 * @param {Number} maxLength - Maximum length
 * @param {String} suffix - Suffix to add (default: '...')
 * @returns {String} Truncated text
 */
export function truncateText(text, maxLength, suffix = '...') {
  if (!text || text.length <= maxLength) return text
  return text.slice(0, maxLength - suffix.length) + suffix
}

/**
 * Format file size in bytes to human readable format
 * 格式化文件大小为可读格式
 * 
 * @param {Number} bytes - File size in bytes
 * @param {Number} decimals - Number of decimal places (default: 2)
 * @returns {String} Formatted file size (e.g., "1.5 MB")
 */
export function formatFileSize(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i]
}

